<template>
  <v-container class="gym-subscription-list">
    <v-row class="flex-grow-0">
      <v-col>
        <v-menu ref="datePickerMenu"
                v-model="datePickerMenuOpened"
                :close-on-content-click="false"
                transition="scale-transition"
                bottom>
          <template v-slot:activator="{on, attrs}">
            <v-text-field outlined
                          v-model="selectedMonth"
                          append-icon="mdi-calendar"
                          readonly
                          hide-details
                          v-on="on"></v-text-field>
          </template>
          <v-date-picker type="month"
                         no-title
                         scrollable
                         v-model="selectedMonth"
                         @input="datePickerMenuOpened = false">
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <RemotePaginatedList :filter-options="filterOptions"
                         :headers="subscriptionHeaders"
                         multi-sort
                         data-url="aafb/admin/gym_subscription/"
                         data-key="subscriptions"
                         :refresh-trigger.sync="innerRefreshTrigger"
                         :extra-params="{from_date: selectedMonthStart, to_date: selectedMonthEnd}"
                         v-bind="$attrs">
      <template v-slot:item.reference_number="{value}">
        {{ "GYM" + value.toString().padStart(8, '0') }}
      </template>
      <template v-slot:item.create_at="{value}">
        {{ $parseDate(value, 'yyyy-MM-dd HH:mm:ss') }}
      </template>
      <template v-slot:item.valid_until="{value}">
        {{ value ? parsePassValidUntil(value) : '' }}
      </template>
      <template v-slot:item.status="{item}">
        {{ $t(BOOKING_STATUS_LABEL[item.status]) }}
      </template>
      <template v-slot:item.account="{item}">
        {{ $t('name', {given_name: item.account.given_name, surname: item.account.surname}) }}
      </template>
      <template v-slot:item.actions="{item}">
        <v-btn icon color="primary"
               @click="viewSubscription(item)">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.cancel="{item}">
        <v-btn v-if="item.status === 1" icon color="primary"
               @click="cancelSubscription(item)">
          <v-icon>fas fa-trash</v-icon>
        </v-btn>
      </template>
    </RemotePaginatedList>


    <GymSubscriptionDetailModal :shown="detailModalShown"
                                :subscription-id="showingSubscriptionId"
                                @cancel="detailModalShown = false"></GymSubscriptionDetailModal>
  </v-container>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core'
import {faEdit, faSearch} from '@fortawesome/free-solid-svg-icons'
import {DateTime} from 'luxon'
import {computed, onMounted, ref} from "@vue/composition-api";
import {BOOKING_STATUS_LABEL} from "@/constants";
import _ from "lodash";
import RemotePaginatedList from "@/components/RemotePaginatedList";
import GymSubscriptionDetailModal from "@/components/acc_booking/GymSubscriptionDetailModal";

library.add(faEdit, faSearch);

export default {
  name: 'AdminGymSubscriptionList',
  components: {GymSubscriptionDetailModal, RemotePaginatedList},
  props: {
    userId: String,
  },
  setup(props, {root}) {
    const innerRefreshTrigger = ref(false);
    const refresh = async function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      refresh();
    });
    const datePickerMenuOpened = ref(false);
    const selectedMonth = ref(DateTime.fromJSDate(new Date()).toFormat('yyyy-MM'));
    const selectedMonthStart = computed(() => {
      return DateTime.fromISO(selectedMonth.value).startOf('month').toISODate();
    });
    const selectedMonthEnd = computed(() => {
      return DateTime.fromISO(selectedMonth.value).endOf('month').toISODate();
    });

    const searchInput = ref('');
    const filter = function (value, search, item) {
      let filteringFields = [
        'reference_number',
        'account.given_name_en', 'account.given_name_zh',
        'account.surname_en', 'account.surname_zh',
      ];

      let input = this.searchInput.toLowerCase();
      return _.some(filteringFields, function (path) {
        let value = root.$resolve(item, path);
        return value && value.toString().toLowerCase().indexOf(input) !== -1
      })
    }

    const filterOptions = computed(() => {
      return [
        {text: root.$t('gym_subscription.reference_number'), value: 'reference_number', contains: true},
        {text: root.$t('admin_user.serial_no'), value: 'account.hkiarace_account.id', contains: true},
        {text: root.$t('admin_user.sasv_name'), value: 'account.hkiarace_account.sasv.name', contains: true},
      ];
    })

    const subscriptionHeaders = ref([
      {text: root.$t('admin_acc_booking.datetime'), value: 'create_at'},
      {text: root.$t('gym_subscription.reference_number'), value: 'reference_number'},
      {text: root.$t('gym_subscription.account'), value: 'account'},
      {text: root.$t('gym_subscription.type'), value: 'type.name'},
      {text: root.$t('gym_subscription.status'), value: 'status'},
      {text: '', value: 'actions'},
      {test: '', value: 'cancel'}
    ]);
    const subscriptionStatus = function (item) {
      return root.$t(BOOKING_STATUS_LABEL[item.status]);
    };

    const showingSubscriptionId = ref(null);
    const detailModalShown = ref(false);
    const viewSubscription = function (item) {
      showingSubscriptionId.value = item.id;
      detailModalShown.value = true;
    }

    const cancelSubscription = function (item) {
      if (confirm(root.$t('admin_acc_gym_subscription.confirm_cancel_subscription_msg'))) {
        root.$callAPI(`aafb/admin/gym_subscription/cancel/`, {
          id : item.id
        }, () => {
          refresh();
        })
      }
    }

    return {
      datePickerMenuOpened,
      selectedMonth,
      selectedMonthStart,
      selectedMonthEnd,
      filter,

      fromISO: DateTime.fromISO,
      filterOptions,
      subscriptionHeaders,
      subscriptionStatus,

      showingSubscriptionId,
      detailModalShown,
      viewSubscription,
      cancelSubscription,

      BOOKING_STATUS_LABEL,
      innerRefreshTrigger,
    }
  },
}
</script>

<style lang="less">
.gym-subscription-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

</style>
